import { ArrowDownOutlined, ArrowUpOutlined, LineChartOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { Button, Col, Row, Select, DatePicker, message, Skeleton } from 'antd';
import { useEffect, useState } from 'react';
import { getJobApplicationsSource } from '../../utils/API';
import dayjs from 'dayjs';

const JobApplicationAnalytics = () => {
    const [source, setSource] = useState<any>([]);
    const [leadsByCategoryChartOptions, setLeadsByCategoryChartOptions] = useState<any>(null);
    const [leadsBarChartOptions, setLeadsBarChartOptions] = useState<any>(null); // Renamed for clarity
    const [leadsLineChartOptions, setLeadsLineChartOptions] = useState<any>(null); // New state for line chart
    const [metrics, setMetrics] = useState<any[]>([]);
    // 🔹 Filters
    const [timeFilter, setTimeFilter] = useState<"today" | "thisMonth" | "lastMonth" | "custom" | undefined>(undefined);
    const [startDate, setStartDate] = useState<any>(null);
    const [endDate, setEndDate] = useState<any>(null);
    const [tempStartDate, setTempStartDate] = useState<any>(null);
    const [tempEndDate, setTempEndDate] = useState<any>(null);
    const [showRangePicker, setShowRangePicker] = useState(false);

    const timeFilterOptions = [
        { value: 'today', label: 'Today' },
        { value: 'thisMonth', label: 'This Month' },
        { value: 'lastMonth', label: 'Last Month' },
        { value: 'custom', label: 'Custom Range' },
    ];

    const fetchLeads = async (params: any = {}) => {
        try {
            const currentTimeFilter = params.timeFilter || timeFilter;
            const currentStartDate = startDate?.format?.('YYYY-MM-DD');
            const currentEndDate = endDate?.format?.('YYYY-MM-DD');
            const res = await getJobApplicationsSource(currentTimeFilter, currentStartDate, currentEndDate);

            // ✅ Filter only "JobApplication"
            const salesLeads = res.filter((lead: any) =>
                Array.isArray(lead.category) && lead.category.includes("JobApplication")
            );
            setSource(salesLeads);

            // --- Pie Chart ---
            const chartData = salesLeads.map((lead: any) => ({
                name: lead.sourceName || 'Unknown',
                value: lead.applicationCount || 0,
                color: lead.sourceName === "Company Website" ? "#FFD700" : "#98FB98",
            }));

            if (chartData.length === 0) {
                chartData.push({ name: "No Data", value: 1, color: "#98FB98" });
            }

            // Sort chartData by value (applications count) descending
            const sortedChartData = [...chartData].sort((a, b) => b.value - a.value);

            setLeadsByCategoryChartOptions({
                title: {
                    text: 'Applications by Source',
                    left: 'left',
                    textStyle: { fontSize: 16, fontWeight: 600 },
                },
                tooltip: {
                    trigger: 'item',
                    formatter: '{a} <br/>{b}: {c} ({d}%)',
                },
                legend: {
                    orient: "vertical",
                    right: 0,
                    top: "center",
                    formatter: (name: string) => {
                        const item = chartData.find((d: any) => d.name === name);
                        if (item) {
                            return `${name} | ${item.value?.toLocaleString()}`;
                        }
                        return name;
                    },
                    textStyle: { fontSize: 14, color: "#000" }
                },
                series: [
                    {
                        name: 'Applications by Source',
                        type: 'pie',
                        radius: ['40%', '70%'],
                        center: ['40%', '55%'],
                        avoidLabelOverlap: false,
                        label: {
                            show: true,
                            color: '#000',
                            formatter: '{b}\n{c} ({d}%)',
                            fontSize: 13,
                            fontWeight: 'bold',
                        },
                        data: sortedChartData, // ✅ use sorted data
                    },
                ],
            });


            // --- Growth & Leads Enrichment ---
            const now = dayjs();
            const todayStart = now.startOf('day');
            const thisMonthStart = now.startOf('month');
            const lastMonthStart = now.subtract(1, 'month').startOf('month');
            const lastMonthEnd = now.subtract(1, 'month').endOf('month');

            const enrichedLeads = salesLeads.map((lead: any) => {
                let filteredLeads = lead.applicationCount || 0;
                let valueChange = 0;
                let growthPercentage = 0;

                if (currentTimeFilter === 'today') {
                    filteredLeads = lead.applicationDates.filter((d: string) =>
                        dayjs(d).isSame(todayStart, 'day')
                    ).length;
                } else if (currentTimeFilter === 'thisMonth') {
                    filteredLeads = lead.applicationDates.filter((d: string) =>
                        dayjs(d).isAfter(thisMonthStart) || dayjs(d).isSame(thisMonthStart, 'day')
                    ).length;
                } else if (currentTimeFilter === 'lastMonth') {
                    filteredLeads = lead.applicationDates.filter((d: string) =>
                        (dayjs(d).isAfter(lastMonthStart) || dayjs(d).isSame(lastMonthStart, 'day')) &&
                        (dayjs(d).isBefore(lastMonthEnd) || dayjs(d).isSame(lastMonthEnd, 'day'))
                    ).length;
                } else if (currentTimeFilter === 'custom' && currentStartDate && currentEndDate) {
                    filteredLeads = lead.applicationDates.filter((d: string) =>
                        (dayjs(d).isAfter(dayjs(currentStartDate)) || dayjs(d).isSame(dayjs(currentStartDate), 'day')) &&
                        (dayjs(d).isBefore(dayjs(currentEndDate)) || dayjs(d).isSame(dayjs(currentEndDate), 'day'))
                    ).length;
                } else {
                    filteredLeads = lead.applicationCount || 0;
                }

                if (currentTimeFilter === 'lastMonth') {
                    valueChange = 0;
                    growthPercentage = 0;
                } else {
                    const thisMonthLeads = lead.applicationDates.filter((d: string) =>
                        dayjs(d).isAfter(thisMonthStart) || dayjs(d).isSame(thisMonthStart, 'day')
                    ).length;
                    const lastMonthLeads = lead.applicationDates.filter((d: string) =>
                        (dayjs(d).isAfter(lastMonthStart) || dayjs(d).isSame(lastMonthStart, 'day')) &&
                        (dayjs(d).isBefore(lastMonthEnd) || dayjs(d).isSame(lastMonthEnd, 'day'))
                    ).length;
                    valueChange = thisMonthLeads - lastMonthLeads;
                    growthPercentage = lastMonthLeads > 0
                        ? Number(((valueChange / lastMonthLeads) * 100).toFixed(1))
                        : thisMonthLeads > 0 ? 100 : 0;
                }

                if (currentTimeFilter === 'custom' && currentStartDate && currentEndDate) {
                    const prevStartDate = dayjs(currentStartDate).subtract(
                        dayjs(currentEndDate).diff(dayjs(currentStartDate), 'day'), 'day'
                    );
                    const prevEndDate = dayjs(currentStartDate).subtract(1, 'day');
                    const currentLeads = lead.applicationDates.filter((d: string) =>
                        (dayjs(d).isAfter(dayjs(currentStartDate)) || dayjs(d).isSame(dayjs(currentStartDate), 'day')) &&
                        (dayjs(d).isBefore(dayjs(currentEndDate)) || dayjs(d).isSame(dayjs(currentEndDate), 'day'))
                    ).length;
                    const prevLeads = lead.applicationDates.filter((d: string) =>
                        (dayjs(d).isAfter(prevStartDate) || dayjs(d).isSame(prevStartDate, 'day')) &&
                        (dayjs(d).isBefore(prevEndDate) || dayjs(d).isSame(prevEndDate, 'day'))
                    ).length;
                    valueChange = currentLeads - prevLeads;
                    growthPercentage = prevLeads > 0
                        ? Number(((valueChange / prevLeads) * 100).toFixed(1))
                        : currentLeads > 0 ? 100 : 0;
                }

                return {
                    ...lead,
                    filteredLeads,
                    valueChange,
                    growthPercentage,
                };
            });

            // --- Sort enrichedLeads by filteredLeads (high → low) ---
            const sortedLeads = [...enrichedLeads].sort(
                (a: any, b: any) => (b.filteredLeads || 0) - (a.filteredLeads || 0)
            );

            // --- Bar Chart Data ---
            const barChartCategories = sortedLeads.map((lead: any) => lead.sourceName || 'Unknown');
            const barChartValues = sortedLeads.map((lead: any) => lead.filteredLeads || 0);
            const growthPercentages = sortedLeads.map((lead: any) =>
                lead.growthPercentage >= 0
                    ? `↑ ${Math.abs(lead.growthPercentage)}%`
                    : `↓ ${Math.abs(lead.growthPercentage)}%`
            );

            setLeadsBarChartOptions({
                title: {
                    left: 'center',
                    textStyle: { fontSize: 16, fontWeight: 600 }
                },
                grid: { left: '5%', right: '10%', containLabel: true },
                xAxis: { type: 'value', show: false, splitLine: { show: false } },
                yAxis: [
                    {
                        type: 'category',
                        data: barChartCategories,
                        axisTick: { show: false },
                        axisLine: { show: true },
                        axisLabel: { fontSize: 14 },
                        inverse: true // 🔥 keeps highest at top
                    },
                    {
                        type: 'category',
                        position: 'right',
                        offset: 50,
                        data: growthPercentages,
                        axisLine: { show: false },
                        axisTick: { show: false },
                        axisLabel: {
                            fontSize: 14,
                            fontWeight: 'bold',
                            color: (val: string) => (val.includes('↓') ? 'red' : 'green')
                        },
                        inverse: true // 🔥 aligns with main axis
                    }
                ],
                series: [
                    {
                        type: 'bar',
                        data: barChartValues,
                        barWidth: 30,
                        barCategoryGap: '0%',
                        itemStyle: { color: '#409EFF' },
                        label: {
                            show: true,
                            position: 'right',
                            distance: 8,
                            formatter: '{c}',
                            fontSize: 14,
                            color: '#000'
                        }
                    }
                ]
            });

            // --- Collect unique dates across all sources ---
            const allDates = Array.from(
                new Set(
                    enrichedLeads.flatMap((lead: any) =>
                        lead.applicationDates.map((d: string) => dayjs(d).format("YYYY-MM-DD"))
                    )
                )
            ).sort(); // sorted list of all dates

            // --- Collect unique sources ---
            const allSources = Array.from(
                new Set(enrichedLeads.map((lead: any) => lead.sourceName || "Unknown"))
            );

            // --- Create one line series per source ---
            const lineSeries = allSources.map((source) => ({
                name: source,
                type: "line",
                smooth: true,
                symbol: "circle",
                lineStyle: { width: 2 },
                data: allDates.map((date) => {
                    const match = enrichedLeads.find(
                        (lead: any) =>
                            (lead.sourceName || "Unknown") === source &&
                            lead.applicationDates.some(
                                (d: string) => dayjs(d).format("YYYY-MM-DD") === date
                            )
                    );
                    return match ? match.applicationCount || 0 : 0;
                }),
            }));

            // --- Find max dynamically (across all series) ---
            const allValues = lineSeries.flatMap((s) => s.data);
            const maxValue = Math.max(...allValues, 0);

            setLeadsLineChartOptions({
                title: {
                    text: "Applications by Source",
                    left: "center",
                    textStyle: { fontSize: 16, fontWeight: 600 },
                },
                tooltip: { trigger: "axis" },
                legend: { top: 30, type: "scroll" },
                xAxis: {
                    type: "category",
                    data: allDates,
                    axisLabel: { rotate: allDates.length > 7 ? 45 : 0 },
                },
                yAxis: {
                    type: "value",
                    min: 0,
                    max: maxValue + 8, // dynamic
                    name: "Number of Applications",
                },
                series: lineSeries,
            });

            // --- Cards ---
            const dynamicMetrics = enrichedLeads
                .map((lead: any) => ({
                    title: lead.sourceName || "Unknown",
                    Total: lead.applicationCount || 0,
                    filteredLeads: lead.filteredLeads || 0,
                    valueChange: Math.abs(lead.valueChange),
                    change: {
                        text: `${Math.abs(lead.growthPercentage)}%`,
                        style: {
                            color: lead.growthPercentage >= 0 ? '#32CD32' : '#FF4500',
                            fontWeight: 'bold',
                        },
                        icon: lead.growthPercentage >= 0 ? (
                            <ArrowUpOutlined style={{ color: '#32CD32' }} />
                        ) : (
                            <ArrowDownOutlined style={{ color: '#FF4500' }} />
                        ),
                    },
                }))
                // ✅ Sort by applicationCount (Total) descending
                .sort((a: any, b: any) => b.Total - a.Total);

            setMetrics(dynamicMetrics);

        } catch (err) {
            message.error("Failed to fetch JobApplicationSource");
        }
    };

    useEffect(() => {
        fetchLeads();
    }, [timeFilter, startDate, endDate]);

    return (
        <>
            <Row gutter={[12, 12]}>
                {/* Filters Row */}
                <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <Select
                        style={{ width: 200 }}
                        value={timeFilter}
                        onChange={(value) => {
                            setTimeFilter(value);
                            if (value === 'custom') {
                                setShowRangePicker(true);
                            } else {
                                setStartDate(null);
                                setEndDate(null);
                                setTempStartDate(null);
                                setTempEndDate(null);
                                setShowRangePicker(false);
                            }
                        }}
                        placeholder="Select Time Filter"
                        allowClear
                        options={timeFilterOptions}
                        dropdownRender={(menu) => (
                            <div>
                                {menu}
                                <div style={{ textAlign: 'center' }}>
                                    <Button
                                        type="link"
                                        style={{ color: 'red' }}
                                        onClick={() => {
                                            setTimeFilter(undefined);
                                            setStartDate(null);
                                            setEndDate(null);
                                            setTempStartDate(null);
                                            setTempEndDate(null);
                                            setShowRangePicker(false);
                                            fetchLeads();
                                        }}
                                    >
                                        Clear All Filters
                                    </Button>
                                </div>
                            </div>
                        )}
                    />
                </Col>

                {/* Custom Range Picker */}
                {showRangePicker && (
                    <div
                        style={{
                            position: 'fixed',
                            top: '25%',
                            left: '70%',
                            transform: 'translate(-10%, -50%)',
                            zIndex: 1500,
                            background: '#fff',
                            padding: '16px',
                            boxShadow:
                                '0 3px 6px -4px rgba(0,0,0,0.12), 0 6px 16px 0 rgba(0,0,0,0.08), 0 9px 28px 8px rgba(0,0,0,0.05)',
                            borderRadius: '8px',
                            width: 'fit-content',
                        }}
                    >
                        <DatePicker.RangePicker
                            value={tempStartDate && tempEndDate ? [tempStartDate, tempEndDate] : null}
                            onChange={(dates) => {
                                setTempStartDate(dates ? dates[0] : null);
                                setTempEndDate(dates ? dates[1] : null);
                            }}
                            format="YYYY-MM-DD"
                            style={{ marginBottom: '16px', width: '100%' }}
                            allowClear={false}
                            // 👇 This is the key part
                            getPopupContainer={(triggerNode) => document.body}
                            popupStyle={{ zIndex: 2000 }} // make sure it’s above your box
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <Button
                                onClick={() => {
                                    setShowRangePicker(false);
                                    setTempStartDate(null);
                                    setTempEndDate(null);
                                    setTimeFilter(undefined);
                                    setStartDate(null);
                                    setEndDate(null);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                style={{ color: "white" }}
                                className='allbuttons'
                                onClick={() => {
                                    if (tempStartDate && tempEndDate) {
                                        setStartDate(tempStartDate);
                                        setEndDate(tempEndDate);
                                        setShowRangePicker(false);
                                    }
                                }}
                                disabled={!tempStartDate || !tempEndDate}
                            >
                                Apply
                            </Button>
                        </div>
                    </div>
                )}

                {/* Cards */}
                <Col span={24}>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                            gap: "16px",
                            padding: "8px",
                        }}
                    >
                        {metrics.length ? (
                            metrics.map((metric, index) => (
                                <div
                                    key={index}
                                    className="relative bg-white rounded-2xl p-6 shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden"
                                >
                                    {/* Top gradient highlight bar */}
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-500 rounded-t-2xl"></div>

                                    <div className="flex justify-between items-start">
                                        {/* Left section */}
                                        <div className="flex items-center space-x-4">
                                            <div className="bg-gradient-to-br from-yellow-100 to-yellow-200 p-1.5 rounded-sm shadow-inner flex items-center justify-center">
                                                <LineChartOutlined style={{ fontSize: "26px", color: "#F59E0B" }} />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold">{metric.filteredLeads}</h2>
                                                <p className="text-gray-500 text-sm">{metric.title}</p>
                                            </div>
                                        </div>

                                        {/* Right section: change */}
                                        <div className="text-right">
                                            <div
                                                className="text-lg font-bold flex items-center gap-2"
                                                style={{ color: metric.change.style.color }}
                                            >
                                                {metric.change.icon}
                                                <span className="text-gray-900 text-xl font-semibold">{metric.valueChange}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Bottom section */}
                                    <div className="flex justify-between mt-2 border-t border-gray-200 pt-3 items-center">
                                        <div className="text-sm">
                                            <p style={metric.change.style}>
                                                {metric.change.icon} {metric.change.text}{" "}
                                                <span className="text-gray-500 font-normal">from last period</span>
                                            </p>
                                        </div>
                                    </div>

                                    {/* Soft decorative circles */}
                                    <div className="absolute -top-5 -right-5 w-16 h-16 bg-purple-100 rounded-full opacity-30 blur-3xl pointer-events-none"></div>
                                    <div className="absolute -bottom-5 -left-5 w-20 h-20 bg-yellow-100 rounded-full opacity-30 blur-3xl pointer-events-none"></div>
                                </div>
                            ))
                        ) : (
                            // 👇 Skeleton for loading state
                            Array.from({ length: 4 }).map((_, index) => (
                                <div
                                    key={index}
                                    className="relative bg-white rounded-2xl p-6 shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden"
                                >
                                    {/* Top placeholder gradient */}
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gray-200 to-gray-300 rounded-t-2xl"></div>

                                    {/* Skeleton content */}
                                    <div className="flex items-center space-x-4">
                                        <Skeleton.Avatar active size={48} shape="square" />
                                        <div className="flex-1">
                                            <Skeleton.Input active style={{ width: "70%", height: 20, marginBottom: 8 }} />
                                            <Skeleton.Input active style={{ width: "50%", height: 16 }} />
                                        </div>
                                    </div>

                                    <div className="mt-4 flex justify-between gap-3 ml-4 ">
                                        <Skeleton.Input active style={{ width: "40%", height: 24 }} />
                                        {/* <Skeleton.Input active style={{ width: "30%", height: 24 }} /> */}
                                    </div>

                                    <div className="absolute -top-5 -right-5 w-16 h-16 bg-gray-100 rounded-full opacity-30 blur-3xl pointer-events-none"></div>
                                    <div className="absolute -bottom-5 -left-5 w-20 h-20 bg-gray-100 rounded-full opacity-30 blur-3xl pointer-events-none"></div>
                                </div>
                            ))
                        )}
                    </div>

                </Col>

                {/* Charts */}
                <Col span={24}>
                    <div className="bg-white p-6 rounded-xl shadow-md">
                        {leadsByCategoryChartOptions ? (
                            <ReactECharts
                                option={leadsByCategoryChartOptions}
                                style={{ height: 400, width: "100%" }}
                            />
                        ) : (
                            <div className="flex flex-col h-[400px] justify-between">
                                <Skeleton.Input active style={{ width: "40%", height: 24, marginBottom: 20 }} />
                                <Skeleton.Button active style={{ width: "100%", height: 300, borderRadius: 12 }} />
                            </div>
                        )}
                    </div>
                </Col>

                <Col span={24}>
                    <div className="bg-white p-6 rounded-xl shadow-md">
                        {leadsBarChartOptions ? (
                            <ReactECharts
                                option={leadsBarChartOptions}
                                style={{ height: 400, width: "100%" }}
                            />
                        ) : (
                            <div className="flex flex-col h-[400px] justify-between">
                                <Skeleton.Input active style={{ width: "40%", height: 24, marginBottom: 20 }} />
                                <Skeleton.Button active style={{ width: "100%", height: 300, borderRadius: 12 }} />
                            </div>
                        )}
                    </div>
                </Col>

                <Col span={24}>
                    <div className="bg-white p-6 rounded-xl shadow-md">
                        {leadsLineChartOptions ? (
                            <ReactECharts
                                option={leadsLineChartOptions}
                                style={{ height: 400, width: "100%" }}
                            />
                        ) : (
                            <div className="flex flex-col h-[400px] justify-between">
                                <Skeleton.Input active style={{ width: "40%", height: 24, marginBottom: 20 }} />
                                <Skeleton.Button active style={{ width: "100%", height: 300, borderRadius: 12 }} />
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </>
    );
};

export default JobApplicationAnalytics;